import { ReactElement, useState } from 'react';

import Modal from '@components/Modal';
import useNoodleApi from '@hooks/useNoodleApi';
import { useToast } from '@/hooks';
import CreateEditAiAgentForm from '@/components/CreateEditAiAgentForm';
import { AiAgent, Expression } from '@/typings/questionnaires-api-models';
import createAiAgent from '@/tsClient/questionnaires/createAiAgent';
import deleteAiAgent from '@/tsClient/questionnaires/deleteAiAgent';
import updateAiAgent from '@/tsClient/questionnaires/updateAiAgent';

import s from './CreateEditAiAgentModal.module.scss';

type Props = {
  aiAgent: AiAgent & { listFilterExpression: Expression | null } | null;
  questionnaireTemplateId: string;
  onClose: () => void;
  onSave: () => Promise<void>;
};

const CreateEditAiAgentModal = ({ aiAgent, questionnaireTemplateId, onSave, onClose }: Props): ReactElement => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getData: createAiAgentFn } = useNoodleApi(createAiAgent, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'AI agent created successfully!'],
  });
  const { getData: updateAiAgentFn } = useNoodleApi(updateAiAgent, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'AI agent updated successfully!'],
  });
  const { getData: deleteAiAgentFn } = useNoodleApi(deleteAiAgent, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'AI agent deleted successfully!'],
  });
  const isEditing = aiAgent?.id;

  const handleSubmit: Parameters<typeof CreateEditAiAgentForm>[0]['onSubmit'] = async agentData => {
    setIsSubmitting(true);
    if (agentData?.id) {
      await updateAiAgentFn({
        agentId: agentData.id,
        aiModel: agentData.aiModel,
        creatorId: agentData.creatorId,
        externalData: agentData.externalData ?? [],
        fieldData: agentData.fieldData ?? [],
        fieldOutputs: agentData.fieldOutputs ?? [],
        groupByFieldKey: agentData.groupByFieldKey,
        instructions: agentData.instructions,
        internalData: agentData.internalData,
        isAutoRunEnabled: agentData.isAutoRunEnabled,
        listFieldData: agentData.listFieldData ?? [],
        listFilterExpression: agentData.listFilterExpression.expression ? agentData.listFilterExpression : null,
        questionnaireTemplateId: agentData.questionnaireTemplateId,
        title: agentData.title,
      });
    } else {
      await createAiAgentFn({
        aiModel: agentData.aiModel,
        creatorId: agentData.creatorId,
        externalData: agentData.externalData ?? [],
        fieldData: agentData.fieldData ?? [],
        fieldOutputs: agentData.fieldOutputs ?? [],
        groupByFieldKey: agentData.groupByFieldKey,
        instructions: agentData.instructions,
        internalData: agentData.internalData,
        isAutoRunEnabled: agentData.isAutoRunEnabled,
        listFieldData: agentData.listFieldData ?? [],
        listFilterExpression: agentData.listFilterExpression.expression ? agentData.listFilterExpression : null,
        questionnaireTemplateId: agentData.questionnaireTemplateId,
        title: agentData.title,
      });
    }
    setIsSubmitting(false);
    onSave();
  };

  const handleDelete = async (): Promise<void> => {
    if (!aiAgent) return;
    if (!window.confirm("Are you sure you want to delete this AI agent?")) return;
    setIsSubmitting(true);
    await deleteAiAgentFn({
      agentId: aiAgent.id,
      creatorId: aiAgent.creatorId,
      questionnaireTemplateId: aiAgent.questionnaireTemplateId,
    });
    setIsSubmitting(false);
    await onSave();
    onClose();
  };

  return (
    <Modal title={isEditing ? 'Edit Ai Agent' : 'Create Ai Agent'} onClose={onClose}>
      <div className={s.createServiceModalPage}>
        <CreateEditAiAgentForm
          aiAgent={aiAgent}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          isSubmitting={isSubmitting}
          questionnaireTemplateId={questionnaireTemplateId}
        />
      </div>
    </Modal>
  );
};

export default CreateEditAiAgentModal;
